/*  ListUrBussiness.jsx  ––  Redesigned (2025)
   • Clean multi-step feel with clear sections
   • Business images: up to 5 (first becomes cover)
   • Staff: name per row
   • Full cross-browser + mobile support
*/

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18n';
import './ListUrBussiness.css';
import { http } from '../lib/http';

/* ─── static data ─── */
const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const hourSlots = Array.from({ length: 16 }, (_, i) => `${(i + 8).toString().padStart(2, '0')}:00`);
const towns = [
  'Maadi', 'Masr El Gedida', 'Heliopolis', 'Nasr City',
  'New Cairo', 'October City', 'Zamalek', 'Mohandessin', 'Dokki', 'Giza'
];
const serviceBoxes = [
  'Hair & Nail', 'Barber', 'Tatoo and Piercing',
  'Laser Clinics', 'Brows & lashes', 'Makeup', 'Massage', 'Pet Services'
];
const MAX_IMAGES = 5;

/* ─── step labels ─── */
const STEPS = [
  { num: 1, label: 'Business Info' },
  { num: 2, label: 'Schedule' },
  { num: 3, label: 'Services' },
  { num: 4, label: 'Team & Photos' },
];

export default function ListUrBussiness() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);

  const [form, setForm] = useState({
    name: '', town: '', serviceCategory: '', phone: '', mapEmbed: '',
    imageFiles: [],
    portfolioImages: [],
    services: [{ serviceName: '', price: '', duration: '' }],
    portfolio: ''
  });

  const [staff, setStaff] = useState([{ name: '' }]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [portfolioPreviews, setPortfolioPreviews] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ─── handlers ─── */
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    setError('');
  };

  const handleFileChange = e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length + form.imageFiles.length > MAX_IMAGES) {
      setError(`You can upload up to ${MAX_IMAGES} images.`);
      return;
    }
    setError('');
    setForm(p => ({ ...p, imageFiles: [...p.imageFiles, ...files] }));
    setImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeImage = idx => {
    setForm(p => { const c = [...p.imageFiles]; c.splice(idx, 1); return { ...p, imageFiles: c }; });
    setImagePreviews(prev => { const c = [...prev]; c.splice(idx, 1); return c; });
  };

  const handlePortfolioFileChange = e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length + form.portfolioImages.length > 10) {
      setError('You can upload up to 10 portfolio images.');
      return;
    }
    setError('');
    setForm(p => ({ ...p, portfolioImages: [...p.portfolioImages, ...files] }));
    setPortfolioPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removePortfolioImage = idx => {
    setForm(p => { const c = [...p.portfolioImages]; c.splice(idx, 1); return { ...p, portfolioImages: c }; });
    setPortfolioPreviews(prev => { const c = [...prev]; c.splice(idx, 1); return c; });
  };

  const handleServiceChange = (i, e) => {
    const { name, value } = e.target;
    setForm(p => {
      const copy = [...p.services];
      copy[i] = { ...copy[i], [name]: value };
      return { ...p, services: copy };
    });
  };
  const addServiceField = () => setForm(p => ({ ...p, services: [...p.services, { serviceName: '', price: '', duration: '' }] }));
  const removeServiceField = i => setForm(p => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }));

  const toggleDay = d => setAvailableDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);
  const toggleSlot = s => setAvailableSlots(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const selectAllDays = () => setAvailableDays(availableDays.length === weekDays.length ? [] : [...weekDays]);
  const selectAllSlots = () => setAvailableSlots(availableSlots.length === hourSlots.length ? [] : [...hourSlots]);

  const handleStaffName = (i, val) => {
    setStaff(list => list.map((m, idx) => idx === i ? { ...m, name: val } : m));
  };
  const addStaffRow = () => setStaff(list => [...list, { name: '' }]);
  const removeStaffRow = i => setStaff(list => list.filter((_, idx) => idx !== i));

  /* ─── step validation ─── */
  const validateStep = (step) => {
    if (step === 1) {
      if (!form.name.trim()) return 'Please enter your shop name.';
      if (!form.town) return 'Please select a town.';
      if (!form.serviceCategory) return 'Please select a service category.';
      if (!form.phone.trim()) return 'Please enter your shop phone number.';
      if (!form.mapEmbed.trim()) return 'Please enter a map embed URL.';
    }
    if (step === 2) {
      if (!availableDays.length) return 'Please select at least one available day.';
      if (!availableSlots.length) return 'Please select at least one time slot.';
    }
    if (step === 3) {
      if (form.services.some(s => !s.serviceName.trim() || !s.price.trim() || !s.duration.trim()))
        return 'Please fill in all service fields (name, price, duration).';
    }
    if (step === 4) {
      if (!form.imageFiles.length) return 'Please upload at least one business photo.';
    }
    return '';
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) { setError(err); return; }
    setError('');
    setCurrentStep(s => Math.min(s + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setError('');
    setCurrentStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ─── submit ─── */
  const handleSubmit = async e => {
    e.preventDefault();
    const err = validateStep(4);
    if (err) { setError(err); return; }

    const completedStaff = staff.filter(s => s.name.trim());
    setError(''); setLoading(true);

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'services') fd.append('services', JSON.stringify(v));
      else if (k === 'imageFiles') v.forEach(f => fd.append('imageFiles', f));
      else if (k === 'portfolioImages') v.forEach(f => fd.append('portfolioImages', f));
      else fd.append(k, v);
    });

    const availability = {};
    availableDays.forEach(d => availability[d] = [...availableSlots]);
    fd.append('availability', JSON.stringify(availability));
    fd.append('exceptions', JSON.stringify([]));

    if (completedStaff.length) {
      fd.append('staff', JSON.stringify(completedStaff.map(s => ({ name: s.name.trim() }))));
    }

    try {
      const resp = await http.fetch('/businesses', { method: 'POST', body: fd });
      if (!resp.ok) {
        const text = await resp.text();
        let msg = text;
        try { const j = JSON.parse(text); if (j?.message) msg = j.message; } catch { }
        throw new Error(msg);
      }
      setSuccessData(await resp.json());
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const goToDashboard = () => navigate('/ShopOwnerDashboard', { state: { generatedAccount: successData.generatedAccount } });

  /* ─── success screen ─── */
  if (successData) {
    return (
      <div className="lub-page">
        <div className="lub-success-card">
          <div className="lub-success-icon">✓</div>
          <h2>You're Listed!</h2>
          <p>Your business has been successfully submitted. Here are your login credentials:</p>
          <div className="lub-credentials">
            <div className="lub-credential-row">
              <span className="lub-credential-label">Shop Name</span>
              <span className="lub-credential-value">{successData.generatedAccount.shopName}</span>
            </div>
            <div className="lub-credential-row">
              <span className="lub-credential-label">Password</span>
              <span className="lub-credential-value lub-password">{successData.generatedAccount.password}</span>
            </div>
          </div>
          <button className="lub-btn-primary lub-btn-full" onClick={goToDashboard}>
            Go to My Dashboard →
          </button>
        </div>
      </div>
    );
  }

  /* ─── main form ─── */
  return (
    <div className="lub-page">
      <div className="lub-wrapper">

        {/* Header */}
        <div className="lub-header">
          <h1 className="lub-title">List Your Business</h1>
          <p className="lub-subtitle">Join thousands of shops already on our platform</p>
        </div>

        {/* Step indicator */}
        <div className="lub-steps">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className={`lub-step ${currentStep === step.num ? 'active' : ''} ${currentStep > step.num ? 'done' : ''}`}>
                <div className="lub-step-circle">
                  {currentStep > step.num ? '✓' : step.num}
                </div>
                <span className="lub-step-label">{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`lub-step-line ${currentStep > step.num ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="lub-card">

          {/* Error banner */}
          {error && (
            <div className="lub-error-banner" role="alert">
              <span className="lub-error-icon">!</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>

            {/* ── STEP 1: Business Info ── */}
            {currentStep === 1 && (
              <div className="lub-section">
                <div className="lub-section-header">
                  <div className="lub-section-number">1</div>
                  <div>
                    <h2 className="lub-section-title">Business Information</h2>
                    <p className="lub-section-desc">Tell us the basics about your shop</p>
                  </div>
                </div>

                <div className="lub-grid-2">
                  <div className="lub-field">
                    <label className="lub-label" htmlFor="name">
                      Shop Name <span className="lub-required">*</span>
                    </label>
                    <input
                      id="name" name="name" className="lub-input"
                      placeholder="e.g. Cairo Cuts Barbershop"
                      value={form.name} onChange={handleChange} required
                    />
                  </div>

                  <div className="lub-field">
                    <label className="lub-label" htmlFor="phone">
                      Phone Number <span className="lub-required">*</span>
                    </label>
                    <input
                      id="phone" name="phone" className="lub-input"
                      placeholder="+20 1XX XXX XXXX"
                      value={form.phone} onChange={handleChange} required
                    />
                  </div>
                </div>

                <div className="lub-grid-2">
                  <div className="lub-field">
                    <label className="lub-label" htmlFor="town">
                      Town / Area <span className="lub-required">*</span>
                    </label>
                    <div className="lub-select-wrap">
                      <select
                        id="town" name="town" className="lub-select"
                        value={form.town} onChange={handleChange} required
                      >
                        <option value="" disabled hidden>Select your area…</option>
                        {towns.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <span className="lub-select-arrow" aria-hidden>▾</span>
                    </div>
                  </div>

                  <div className="lub-field">
                    <label className="lub-label" htmlFor="serviceCategory">
                      Category <span className="lub-required">*</span>
                    </label>
                    <div className="lub-select-wrap">
                      <select
                        id="serviceCategory" name="serviceCategory" className="lub-select"
                        value={form.serviceCategory} onChange={handleChange} required
                      >
                        <option value="" disabled hidden>Select a category…</option>
                        {serviceBoxes.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <span className="lub-select-arrow" aria-hidden>▾</span>
                    </div>
                  </div>
                </div>

                <div className="lub-field">
                  <label className="lub-label" htmlFor="mapEmbed">
                    Google Maps Link <span className="lub-required">*</span>
                  </label>
                  <input
                    id="mapEmbed" name="mapEmbed" className="lub-input"
                    placeholder="https://maps.google.com/…"
                    value={form.mapEmbed} onChange={handleChange} required
                  />
                  <span className="lub-hint">Paste the URL from Google Maps share → embed</span>
                </div>
              </div>
            )}

            {/* ── STEP 2: Schedule ── */}
            {currentStep === 2 && (
              <div className="lub-section">
                <div className="lub-section-header">
                  <div className="lub-section-number">2</div>
                  <div>
                    <h2 className="lub-section-title">Working Schedule</h2>
                    <p className="lub-section-desc">Choose your available days and time slots</p>
                  </div>
                </div>

                <div className="lub-field">
                  <div className="lub-label-row">
                    <label className="lub-label">Available Days <span className="lub-required">*</span></label>
                    <button type="button" className="lub-select-all-btn" onClick={selectAllDays}>
                      {availableDays.length === weekDays.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="lub-days-grid">
                    {weekDays.map(d => (
                      <button
                        key={d} type="button"
                        className={`lub-day-chip ${availableDays.includes(d) ? 'active' : ''}`}
                        onClick={() => toggleDay(d)}
                      >
                        {d.charAt(0).toUpperCase() + d.slice(1, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="lub-field">
                  <div className="lub-label-row">
                    <label className="lub-label">Available Hours <span className="lub-required">*</span></label>
                    <button type="button" className="lub-select-all-btn" onClick={selectAllSlots}>
                      {availableSlots.length === hourSlots.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="lub-slots-grid">
                    {hourSlots.map(s => (
                      <button
                        key={s} type="button"
                        className={`lub-slot-chip ${availableSlots.includes(s) ? 'active' : ''}`}
                        onClick={() => toggleSlot(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {availableDays.length > 0 && availableSlots.length > 0 && (
                  <div className="lub-schedule-summary">
                    <strong>{availableDays.length} day{availableDays.length > 1 ? 's' : ''}</strong> · <strong>{availableSlots.length} slot{availableSlots.length > 1 ? 's' : ''}</strong> per day
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 3: Services ── */}
            {currentStep === 3 && (
              <div className="lub-section">
                <div className="lub-section-header">
                  <div className="lub-section-number">3</div>
                  <div>
                    <h2 className="lub-section-title">Services Offered</h2>
                    <p className="lub-section-desc">Add every service you provide with pricing</p>
                  </div>
                </div>

                <div className="lub-services-list">
                  {form.services.map((s, i) => (
                    <div className="lub-service-card" key={i}>
                      <div className="lub-service-card-header">
                        <span className="lub-service-badge">Service {i + 1}</span>
                        {form.services.length > 1 && (
                          <button type="button" className="lub-remove-btn" onClick={() => removeServiceField(i)} aria-label="Remove service">
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="lub-grid-3">
                        <div className="lub-field">
                          <label className="lub-label" htmlFor={`serviceName-${i}`}>Service Name</label>
                          <input
                            id={`serviceName-${i}`} name="serviceName" className="lub-input"
                            placeholder="e.g. Haircut"
                            value={s.serviceName} onChange={e => handleServiceChange(i, e)} required
                          />
                        </div>
                        <div className="lub-field">
                          <label className="lub-label" htmlFor={`price-${i}`}>Price (EGP)</label>
                          <input
                            id={`price-${i}`} name="price" className="lub-input"
                            placeholder="e.g. 150"
                            value={s.price} onChange={e => handleServiceChange(i, e)} required
                          />
                        </div>
                        <div className="lub-field">
                          <label className="lub-label" htmlFor={`duration-${i}`}>Duration (min)</label>
                          <input
                            id={`duration-${i}`} name="duration" className="lub-input"
                            placeholder="e.g. 30"
                            value={s.duration} onChange={e => handleServiceChange(i, e)} required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button type="button" className="lub-add-btn" onClick={addServiceField}>
                  + Add Another Service
                </button>
              </div>
            )}

            {/* ── STEP 4: Team & Photos ── */}
            {currentStep === 4 && (
              <div className="lub-section">
                <div className="lub-section-header">
                  <div className="lub-section-number">4</div>
                  <div>
                    <h2 className="lub-section-title">Team & Photos</h2>
                    <p className="lub-section-desc">Upload your shop photos and add your staff</p>
                  </div>
                </div>

                {/* Business Photos */}
                <div className="lub-field">
                  <label className="lub-label">
                    Business Photos <span className="lub-required">*</span>
                    <span className="lub-count-badge">{form.imageFiles.length}/{MAX_IMAGES}</span>
                  </label>
                  <span className="lub-hint">First photo becomes your cover image</span>

                  {form.imageFiles.length < MAX_IMAGES && (
                    <label className="lub-upload-zone" htmlFor="imageFiles">
                      <span className="lub-upload-icon">📷</span>
                      <span className="lub-upload-text">Click to upload photos</span>
                      <span className="lub-upload-sub">JPG, PNG, WEBP up to 10MB each</span>
                      <input
                        id="imageFiles" type="file" accept="image/*" multiple
                        onChange={handleFileChange} className="lub-file-input"
                        disabled={form.imageFiles.length >= MAX_IMAGES}
                      />
                    </label>
                  )}

                  {imagePreviews.length > 0 && (
                    <div className="lub-preview-grid">
                      {imagePreviews.map((src, idx) => (
                        <div className="lub-preview-cell" key={idx}>
                          {idx === 0 && <span className="lub-cover-badge">Cover</span>}
                          <img src={src} alt={`preview-${idx}`} />
                          <button type="button" className="lub-preview-remove" onClick={() => removeImage(idx)} aria-label="Remove image">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Portfolio Photos */}
                <div className="lub-field">
                  <label className="lub-label">
                    Portfolio / Work Samples
                    <span className="lub-count-badge">{form.portfolioImages.length}/10</span>
                  </label>
                  <span className="lub-hint">Showcase your best work to attract more clients</span>

                  {form.portfolioImages.length < 10 && (
                    <label className="lub-upload-zone lub-upload-zone--secondary" htmlFor="portfolioImages">
                      <span className="lub-upload-icon">🖼️</span>
                      <span className="lub-upload-text">Upload portfolio images</span>
                      <span className="lub-upload-sub">Up to 10 images</span>
                      <input
                        id="portfolioImages" type="file" accept="image/*" multiple
                        onChange={handlePortfolioFileChange} className="lub-file-input"
                        disabled={form.portfolioImages.length >= 10}
                      />
                    </label>
                  )}

                  {portfolioPreviews.length > 0 && (
                    <div className="lub-preview-grid">
                      {portfolioPreviews.map((src, idx) => (
                        <div className="lub-preview-cell" key={idx}>
                          <img src={src} alt={`portfolio-${idx}`} />
                          <button type="button" className="lub-preview-remove" onClick={() => removePortfolioImage(idx)} aria-label="Remove">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Staff Members */}
                <div className="lub-field">
                  <label className="lub-label">Staff Members <span className="lub-optional">(optional)</span></label>
                  <span className="lub-hint">Add your team — clients can pick their preferred barber / stylist</span>

                  <div className="lub-staff-list">
                    {staff.map((m, i) => (
                      <div className="lub-staff-row" key={i}>
                        <div className="lub-staff-avatar" aria-hidden>
                          {m.name ? m.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <input
                          id={`staffName-${i}`} className="lub-input lub-staff-input"
                          placeholder={`Staff member ${i + 1} name`}
                          value={m.name} onChange={e => handleStaffName(i, e.target.value)}
                        />
                        {staff.length > 1 && (
                          <button type="button" className="lub-remove-btn" onClick={() => removeStaffRow(i)} aria-label="Remove staff">✕</button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button type="button" className="lub-add-btn" onClick={addStaffRow}>
                    + Add Staff Member
                  </button>
                </div>
              </div>
            )}

            {/* ── Navigation ── */}
            <div className="lub-nav">
              {currentStep > 1 && (
                <button type="button" className="lub-btn-secondary" onClick={goBack}>
                  ← Back
                </button>
              )}
              {currentStep < 4 ? (
                <button type="button" className="lub-btn-primary lub-btn-next" onClick={goNext}>
                  Continue →
                </button>
              ) : (
                <button type="submit" className="lub-btn-primary lub-btn-next" disabled={loading}>
                  {loading ? <span className="lub-spinner" /> : 'Submit My Business'}
                </button>
              )}
            </div>

          </form>
        </div>

        {/* Progress text */}
        <p className="lub-progress-text">Step {currentStep} of {STEPS.length}</p>

      </div>
    </div>
  );
}